import { DateTime } from "luxon";
import i18n from "i18next";

export const isValueNumber = (value?: number | string | null): boolean => {
  if (typeof value === "number") {
    return isFinite(value);
  }

  if (typeof value === "string") {
    return /^\d+$/.test(value);
  }

  return false;
};

export const isAllowedRAFRoute = (pathname: string) => {
  const allowedRoutes = [
    "/mein-remindme",
    "/mein-remindme/angebote",
    "/mein-remindme/wechselservices",
    "/mein-remindme/wechselservice/strom/detail",
  ];

  const isAllowedRoute = allowedRoutes.some((route) => {
    if (route === "/mein-remindme/wechselservice/strom/detail") {
      // Match the exact route or route with UUID
      return (
        pathname === route ||
        /^\/mein-remindme\/wechselservice\/strom\/detail\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          pathname
        )
      );
    }
    return pathname.endsWith(route);
  });

  return isAllowedRoute;
};

export interface PartialDateParts {
  day?: number;
  month?: number;
  year?: number;
}
export function toDate(parseFormat: string = backendDateFormat) {
  return function toDate(value: PartialDateParts | string) {
    if (typeof value === "string") {
      const date = DateTime.fromFormat(value, parseFormat, { zone: "utc" });
      if (date.isValid) {
        return date.toJSDate();
      }
    } else {
      return fromDateParts(value);
    }
  };
}
export function fromDateParts({ day = 0, month = 0, year = 0 }: PartialDateParts = {}): Date | undefined {
  const date = DateTime.fromObject({ day, month, year }, { zone: "utc" });

  if (date.isValid) {
    return date.toJSDate();
  }
}
const is8Long = isLength(8);
export function isPasswordFormat(value: string) {
  const regX = /^[a-zA-ZäöüÄÖÜß0-9!@#$%^&*()_+\-=\[\]{};:'",.<>\/\\?]{12,}$/;
  return !value || regX.test(value) ? undefined : i18n.t("validation.isPassword");
}

export const isBankCode = [is8Long, isValueNumber];
// eslint-disable-next-line
export const isNumber = function (value: number | string): any {
  return isValueNumber(value) ? undefined : i18n.t("validation.isNumber");
};

export const isGermanAccountNumber = [isNumber, isMinLength(1), isMaxLength(10)];

export const isBic = [
  (n: string) => (n.length >= 8 && n.length <= 11 ? undefined : i18n.t("validation.isBetween8-11")),
];

export function isPhone(value?: string) {
  return typeof value === "string" && /^[0-9 -. ]+$/i.test(value) ? true : false;
}

export function isMaxLength(maxLength: number) {
  return function (value?: string) {
    return typeof value === "string" && value.length > maxLength ? false : true;
  };
}

export function isMinLength(minLength: number) {
  return function (value?: string) {
    return typeof value === "string" && value.length < minLength ? false : true;
  };
}

export function isLength(length: number) {
  return function (value?: string) {
    return typeof value === "string" && value.length === length;
  };
}
export const backendDateFormat = "yyyy-MM-dd";
export const frontendDateFormat = "dd.MM.yyyy";
export const detailedDateFormat = "d. MMMM yyyy";

export const formatForBackend = (date: Date | string, parseFormat: string = frontendDateFormat) => {
  if (!date || (date instanceof Date && date.getFullYear() === 0)) {
    return "";
  }

  let parsedDate;
  if (typeof date === "string") {
    parsedDate = DateTime.fromFormat(date, parseFormat);
  } else {
    parsedDate = DateTime.fromJSDate(date);
  }

  if (!parsedDate.isValid) {
    parsedDate = DateTime.fromFormat(date as string, backendDateFormat);
    if (!parsedDate.isValid) {
      throw Error(
        `Invalid date format for ${date && date.toString()}. Expected format ${parseFormat} or "{backendDateFormat}`
      );
    }
  }

  return parsedDate.toFormat(backendDateFormat);
};

// eslint-disable-next-line
export const isEmpty = (value: any) => {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "object" && Object.keys(value).length === 0) ||
    (typeof value === "string" && value.trim().length === 0)
  );
};
